import { PICKER_DIALOG_STYLE } from 'components/modals/dialogWidths';
import { Search as SearchIcon } from 'components/icons';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { Button } from 'components/ui/button';
import React, { useState } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { FieldValues, useWatch } from 'react-hook-form';
import { MinDto } from 'types/other.ts';
import { useModal } from '../../../modals/useModal.ts';
import DynamicSelectInput, { DynamicSelectInputProps } from '../../inputs/rhf-inputs/DynamicSelectInput';

/** A picker button's name, from its field's label: "Choose Responsible lawyer". */
export const pickerButtonName = ({
    label,
    shouldTranslateLabel = true,
}: {
    label?: string;
    shouldTranslateLabel?: boolean;
}) => (label ? t('choose-field', { field: shouldTranslateLabel ? t(label) : label }) : t('select'));

export type PickerModalProps<TPickedOption, TCreatedOption> = {
    onClose: () => void;
    onConfirm: (selectedItem: TPickedOption) => void;
    onCreateNew: (selectedItem: TCreatedOption) => void;
    searchValue: string;
    currentValue?: TPickedOption[];
    paginatedFilter?: string;
    isOptionEnabled?: (option: TPickedOption) => boolean;
};

type SelectPickerProps<
    TFormData extends FieldValues,
    TOption extends FieldValues,
    TPickedOption,
    TCreatedOption,
    TModalProps extends PickerModalProps<TPickedOption, TCreatedOption> = PickerModalProps<
        TPickedOption,
        TCreatedOption
    >,
> = DynamicSelectInputProps<TFormData, TOption> & {
    PickerModalComponent: React.ComponentType<TModalProps>;
    isPickerModalOptionEnabled?: (option: TPickedOption | TCreatedOption) => boolean;
    /** The search button's own classes, such as a quieter tone where several pickers stack in a table. */
    pickerButtonClassName?: string;
};

/**
 * In a record section the label row runs over the picker button too, 44px with its gap, so the label wraps where
 * the read view's does, which has the whole cell.
 */
const SECTION_PICKER_LABEL_CLASS = '[&>div:first-child]:w-[calc(100%+2.75rem)]';

const DynamicSelectPicker = <
    TFormData extends FieldValues,
    TOption extends FieldValues,
    TPickedOption extends MinDto,
    TCreatedOption extends MinDto,
>({
    pickerButtonClassName,
    ...props
}: SelectPickerProps<TFormData, TOption, TPickedOption, TCreatedOption>) => {
    const { PickerModalComponent } = props;
    const { openModal, closeModal } = useModal();
    const [inputValue, setInputValue] = useState('');
    const currentValue = useWatch({ control: props.control, name: props.name });
    const inSection = useInRecordSection();

    const handleConfirm = (selectedItem: TPickedOption | TCreatedOption) => {
        props.setValue(props.name, selectedItem);
        closeModal();
        setInputValue('');
    };

    const handleCreateNew = (createdOption: TCreatedOption) => {
        if (!props.isPickerModalOptionEnabled || props.isPickerModalOptionEnabled(createdOption)) {
            props.setValue(props.name, createdOption);
        }
        closeModal();
        setInputValue('');
    };

    const handleOpenModal = () => {
        openModal(
            <PickerModalComponent
                onClose={closeModal}
                onConfirm={handleConfirm}
                onCreateNew={handleCreateNew}
                searchValue={inputValue}
                currentValue={currentValue ? [currentValue] : []}
                isOptionEnabled={props.isPickerModalOptionEnabled}
            />,
            closeModal,
            PICKER_DIALOG_STYLE
        );
    };

    const handleSearchChange = (eventType: string, searchValue: string) => {
        if (eventType !== 'blur') {
            setInputValue(searchValue);
        }
        props.onSearchChange?.(eventType, searchValue);
    };

    if (props.readonly) {
        return <DynamicSelectInput {...props} onSearchChange={handleSearchChange} />;
    }

    return (
        // In a record section the field and its button are one cell of the fields grid, which `form-field` says.
        <div className={cn('flex w-full items-start gap-2', inSection && 'form-field')}>
            <div className="min-w-0 flex-1">
                <DynamicSelectInput
                    {...props}
                    wrapperClassName={cn(
                        props.wrapperClassName,
                        inSection && props.label && SECTION_PICKER_LABEL_CLASS
                    )}
                    onSearchChange={handleSearchChange}
                />
            </div>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleOpenModal}
                disabled={props.disabled}
                // Top-aligned and pushed past the label row (28px with its gap, 20px in a record section), so it
                // sits level with the 36px field and stays there when an error line renders under the field.
                className={cn('text-primary', props.label && (inSection ? 'mt-5' : 'mt-7'), pickerButtonClassName)}
                aria-label={pickerButtonName(props)}
            >
                <SearchIcon className="size-5" />
            </Button>
        </div>
    );
};

export default DynamicSelectPicker;
